import {
  IRequestInfoFromAPI,
  REQUEST_INFO_FIELDS,
  RequestInfoField,
  RequestInfoStatus,
} from "@/lib/interfaces and Enums/ENUMRequestInformationStatus";
import { fetchRequestWrapper } from "@/lib/utilities";
import {
  getEditSettingsAndAddToStore,
  patchSingleCPReleaseSetting,
} from "./tradeSettingsStoreACTIONS";
import requestInformationStore, {
  IExecutionRequestInfoState,
  RequestInfoByESID,
} from "./requestInformationStore";
import tradeDataStore from "./tradeDataStore";
import { notifyRequestInformationEvent } from "@/lib/notificationHelper";
import { NotificationNegotiationEvent } from "@/lib/notificationConfig";

// LQC-637 — accepting an RP request releases the matching Advise setting. Maps
// each negotiable request field to the trade-settings field that the Advise
// toggle / Update flow persists, so an accept can drive the SAME settings PATCH.
const REQUEST_FIELD_TO_SETTING: Record<
  RequestInfoField,
  | "releaseDirection"
  | "releaseCompetition"
  | "releaseCover"
  | "releaseCoverSpread"
> = {
  direction: "releaseDirection",
  competition: "releaseCompetition",
  cover: "releaseCover",
  coverSpread: "releaseCoverSpread",
};

// LQC-1842 / LQC-1843 — reverse of REQUEST_FIELD_TO_SETTING. When the OP changes
// an Advise setting via the slide-in Update / bank overrides, this maps the
// changed settings field back to its negotiation field so the request status can
// be reconciled. Iceberg (releaseSize) has no negotiation field, so it is absent.
// LQC-2271 added releaseDirection: the sidebar's Direction column now releases
// the direction directly, which must clear any outstanding RP request for it.
export const SETTING_TO_REQUEST_FIELD: Record<string, RequestInfoField> = {
  releaseDirection: "direction",
  releaseCompetition: "competition",
  releaseCover: "cover",
  releaseCoverSpread: "coverSpread",
};

// LQC-637 — actions for the standalone RP/OP "Request Information" negotiation
// store. This store is fed entirely by its OWN endpoint — it does NOT read the
// trade data / Action Summary payload.
//
// Feed:
//   • Bulk on app load — getAllRequestInformation() (placeholder bulk endpoint).
//   • Per-esid on a SignalR push — refreshRequestInformationForESID() hits
//     /Trade/Execution/Requests/{esid}. Wired in MegaPipe.tsx.
//   • RP request — requestExecutionRequestInformation (tradeSettingsStoreACTIONS)
//     optimistically stamps Requested.
//   • OP response — respondToExecutionRequestInformation() below.
//
// BACKEND CONTRACT (assumed — confirm + adjust the transforms):
//   • GET /Trade/Execution/Requests/{esid} → IRequestInfoEntry[] (one entry per
//     execution request: RP gets its single ER, OP gets all counterparty ERs).
//   • GET /Trade/Execution/Requests (bulk) → IRequestInfoForESID[].

// One execution request's status as the endpoint returns it.
export interface IRequestInfoEntry extends IRequestInfoFromAPI {
  executionRequestId: number | string;
}

// Bulk endpoint groups the entries by esid.
export interface IRequestInfoForESID {
  esid: string;
  requests: IRequestInfoEntry[];
}

// ----- Pure transforms -----------------------------------------------------

// Validate a raw string against the known statuses. Unknown / empty values are
// dropped (the field then defaults to False via the selectors).
function toStatus(raw: unknown): RequestInfoStatus | undefined {
  return (Object.values(RequestInfoStatus) as string[]).includes(raw as string)
    ? (raw as RequestInfoStatus)
    : undefined;
}

// Keep only the four known fields with valid statuses.
export function normalizeRequestInfo(
  raw: IRequestInfoFromAPI | undefined,
): IExecutionRequestInfoState {
  const result: IExecutionRequestInfoState = {};
  if (!raw) return result;
  for (const field of REQUEST_INFO_FIELDS) {
    const status = toStatus(raw[field]);
    if (status) result[field] = status;
  }
  return result;
}

// Turn one esid's array of execution-request entries into the store's per-ER map.
export function buildErMapFromEntries(
  entries: IRequestInfoEntry[] | undefined | null,
): Record<string, IExecutionRequestInfoState> {
  const byErId: Record<string, IExecutionRequestInfoState> = {};
  // Guard with Array.isArray, not just ?? []: the live endpoint can return a
  // non-array (e.g. an object), and `for…of` on that throws "not iterable".
  if (!Array.isArray(entries)) return byErId;
  for (const entry of entries) {
    if (entry?.executionRequestId == null) continue;
    byErId[String(entry.executionRequestId)] = normalizeRequestInfo(entry);
  }
  return byErId;
}

// Turn the bulk endpoint response into the full normalized store shape.
export function buildRequestInfoFromBulk(
  response: IRequestInfoForESID[] | undefined | null,
): RequestInfoByESID {
  const result: RequestInfoByESID = {};
  if (!Array.isArray(response)) return result;
  for (const item of response) {
    if (!item?.esid) continue;
    result[item.esid] = buildErMapFromEntries(item.requests);
  }
  return result;
}

// ----- Notification diff (LQC-637 / LQC-1745) -------------------------------

// Which negotiation notification each interesting field transition produces.
export interface IRequestInfoTransition {
  erId: string;
  field: RequestInfoField;
  event: NotificationNegotiationEvent;
}

// Pure: diff a single esid's before/after ER maps and return the notify-worthy
// field transitions. Self-notification is suppressed for free upstream — the
// acting client optimistically stamps its own action before the push lands, so
// the field is unchanged here for the actor and only transitions for the
// counterparty.
//   • → Requested (was not Requested)  ⇒ RequestReceived  (relevant to OP)
//   • Requested → True                 ⇒ RequestAccepted  (relevant to RP)
//   • Requested → Rejected             ⇒ RequestRejected  (relevant to RP)
export function diffRequestInfoTransitions(
  before: Record<string, IExecutionRequestInfoState> | undefined,
  after: Record<string, IExecutionRequestInfoState> | undefined,
): IRequestInfoTransition[] {
  const transitions: IRequestInfoTransition[] = [];
  const beforeMap = before ?? {};
  const afterMap = after ?? {};

  for (const [erId, erState] of Object.entries(afterMap)) {
    const prevState = beforeMap[erId] ?? {};
    for (const field of REQUEST_INFO_FIELDS) {
      const prev = prevState[field] ?? RequestInfoStatus.False;
      const next = erState[field] ?? RequestInfoStatus.False;
      if (prev === next) continue;

      if (next === RequestInfoStatus.Requested) {
        transitions.push({
          erId,
          field,
          event: NotificationNegotiationEvent.RequestReceived,
        });
      } else if (
        prev === RequestInfoStatus.Requested &&
        next === RequestInfoStatus.True
      ) {
        transitions.push({
          erId,
          field,
          event: NotificationNegotiationEvent.RequestAccepted,
        });
      } else if (
        prev === RequestInfoStatus.Requested &&
        next === RequestInfoStatus.Rejected
      ) {
        transitions.push({
          erId,
          field,
          event: NotificationNegotiationEvent.RequestRejected,
        });
      }
    }
  }
  return transitions;
}

// Only the OP cares about an incoming request; only the RP cares about the
// response to its request.
const EVENT_REQUIRED_ROLE: Record<NotificationNegotiationEvent, "OP" | "RP"> = {
  [NotificationNegotiationEvent.RequestReceived]: "OP",
  [NotificationNegotiationEvent.RequestAccepted]: "RP",
  [NotificationNegotiationEvent.RequestRejected]: "RP",
};

// Fire negotiation notifications for one esid's transitions, gated by the trade's
// role and collapsed to at most one notification per event type (a multi-field
// request shouldn't fire three "Request Received"s).
function fireRequestInfoNotifications(
  esid: string,
  transitions: IRequestInfoTransition[],
): void {
  if (transitions.length === 0) return;

  const trade = tradeDataStore
    .getState()
    .tradeData.find((t) => t.esid === esid);
  const role = trade?.role;
  const owner = trade?.owner;

  const firedEvents = new Set<NotificationNegotiationEvent>();
  for (const { event } of transitions) {
    if (firedEvents.has(event)) continue;
    if (role !== EVENT_REQUIRED_ROLE[event]) continue;
    firedEvents.add(event);
    notifyRequestInformationEvent({ event, esid, role, owner });
  }
}

// ----- Feed: bulk on load + per-esid on push --------------------------------

// Bulk seed on first app load. Co-located in getAllActionSummaryData (the single
// load entry point) but reads its own endpoint — no trade-data coupling.
export async function getAllRequestInformation(
  getAccessTokenSilently: Function,
) {
  // TODO (LQC-637): the /Trade/Execution/Requests payload shape is assumed
  // (IRequestInfoForESID[] — see buildRequestInfoFromBulk). Now that the endpoint
  // is connected, verify the real response matches and adjust the transform if not.
  const response = await fetchRequestWrapper(
    { url: "/Trade/Execution/Requests" },
    getAccessTokenSilently,
  );
  // Only seed from a recognised (array) shape. A non-array means the payload
  // doesn't match the assumed contract yet — seeding {} would blow away any
  // state rather than leaving it untouched. See refresh below for the same rule.
  if (Array.isArray(response)) {
    requestInformationStore
      .getState()
      .setAllRequestInfo(buildRequestInfoFromBulk(response));
  } else if (response) {
    console.warn(
      "[requestInformation] /Trade/Execution/Requests returned a non-array payload; skipping seed until the shape is confirmed.",
      response,
    );
  }
}

// Per-esid refresh — called from the MegaPipe SignalR handler when a negotiation
// push arrives for an esid. Merges just that esid's ER map.
export async function refreshRequestInformationForESID(
  esid: string,
  getAccessTokenSilently: Function,
) {
  const response = await fetchRequestWrapper(
    { url: "/Trade/Execution/Requests/" + esid },
    getAccessTokenSilently,
  );
  // Per-esid shape is a single { esid, requests: IRequestInfoEntry[] } object —
  // NOT an array (the bulk endpoint is the array). Read its `requests`: a real
  // empty array legitimately clears this esid (all resolved), while a payload
  // with no `requests` array is malformed, so leave existing state intact rather
  // than wiping optimistic stamps.
  if (response && Array.isArray(response.requests)) {
    // Diff the authoritative push against current state, then upsert. The diff
    // must be taken BEFORE the upsert so we can see the transition.
    const before = requestInformationStore.getState().requestInfo[esid] ?? {};
    const after = buildErMapFromEntries(response.requests);
    requestInformationStore.getState().upsertRequestInfoForESID(esid, after);
    fireRequestInfoNotifications(esid, diffRequestInfoTransitions(before, after));
  } else if (response) {
    console.warn(
      `[requestInformation] /Trade/Execution/Requests/${esid} payload had no requests[] array; leaving existing request state intact.`,
      response,
    );
  }
}

// ----- Selectors ------------------------------------------------------------

// RP / OP: status of a single field on a single ER (defaults to False).
export function selectFieldStatus(
  esid: string,
  erId: number | string,
  field: RequestInfoField,
) {
  return (state: { requestInfo: RequestInfoByESID }): RequestInfoStatus =>
    state.requestInfo[esid]?.[String(erId)]?.[field] ?? RequestInfoStatus.False;
}

// OP: does ANY counterparty on this ES have a request the OP hasn't actioned?
// Drives the ES-row "incoming request" indicator.
export function selectEsHasPendingRequest(esid: string) {
  return (state: { requestInfo: RequestInfoByESID }): boolean =>
    Object.values(state.requestInfo[esid] ?? {}).some((er) =>
      Object.values(er).includes(RequestInfoStatus.Requested),
    );
}

// Pure: does any trade the user is OP on carry an outstanding RP request? Drives
// the global Trade Settings sidebar-tab pulse — only OP trades count, since the
// RP made the request and is only waiting. Exported for testing.
export function anyOpTradeHasPendingRequest(
  requestInfo: RequestInfoByESID,
  trades: { esid?: string; role?: string }[] | undefined | null,
): boolean {
  return (trades ?? []).some(
    (t) =>
      t?.role === "OP" &&
      t.esid !== undefined &&
      selectEsHasPendingRequest(t.esid)({ requestInfo }),
  );
}

// ----- OP response endpoint -------------------------------------------------

// LQC-637 / LQC-1842 / LQC-1843 — record an accept/reject against the REQUEST
// only. The endpoint takes a BOOLEAN per field (accept = true, reject = false);
// the store keeps the richer status so the UI can tell a decline (Rejected) from
// "never requested" (False). Does NOT touch the settings endpoint or re-hydrate —
// callers that also change a setting (Accept button, or the settings-Update
// reconcile) own that separately, so this stays the single place that talks to
// /Trade/Execution/Request. Exported for testing.
export async function recordRequestResponse({
  esid,
  executionRequestId,
  field,
  accepted,
  getAccessTokenSilently,
}: {
  esid: string;
  executionRequestId: number | string;
  field: RequestInfoField;
  accepted: boolean;
  getAccessTokenSilently: Function;
}) {
  const response = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Request",
      method: "PATCH",
      objToSend: { esid, executionRequestId, [field]: accepted },
    },
    getAccessTokenSilently,
  );

  // Optimistic status stamp so the adjacent status word flips immediately; the
  // backend persists it and returns it on the next /Trade/Execution/Requests push.
  requestInformationStore
    .getState()
    .setFieldStatus(
      esid,
      executionRequestId,
      field,
      accepted ? RequestInfoStatus.True : RequestInfoStatus.Rejected,
    );

  return response;
}

// LQC-637 / LQC-1744 — OP accepts or rejects an RP's request via the strip's
// ✓/✕. Records the response (above) AND, on accept, releases the matching Advise
// setting + re-hydrates the panel so its toggle reflects the release.
export async function respondToExecutionRequestInformation({
  esid,
  executionRequestId,
  field,
  accepted,
  getAccessTokenSilently,
}: {
  esid: string;
  executionRequestId: number | string;
  field: RequestInfoField;
  accepted: boolean;
  getAccessTokenSilently: Function;
}) {
  // 1. Record the accept/reject against the REQUEST (+ optimistic status stamp).
  const response = await recordRequestResponse({
    esid,
    executionRequestId,
    field,
    accepted,
    getAccessTokenSilently,
  });

  // 2. On ACCEPT, releasing the field is the SAME as flipping that CP's matching
  //    Advise toggle to "Yes" and hitting Update — so persist it through the
  //    settings endpoint too. Reject releases nothing, so it skips this.
  if (accepted) {
    await patchSingleCPReleaseSetting({
      esid,
      executionRequestId,
      settingField: REQUEST_FIELD_TO_SETTING[field],
      value: true,
      getAccessTokenSilently,
    });
  }

  // 3. On ACCEPT, the released field changed a trade SETTING (step 2), but the
  //    settings panel store is only hydrated on open — no SignalR push re-fetches
  //    it. So re-run the SAME hydration the panel opened with, pulling the now-
  //    authoritative /Trade/Execution/Edit/Settings/{esid}: the Advise toggle
  //    flips to Yes with a correct baseline (not an orange "pending" optimistic
  //    flip that would re-submit on the next Update). Reject changed no setting.
  //
  //    WHERE THIS COULD GO INSTEAD: this re-hydration fires immediately after the
  //    settings PATCH resolves, which assumes the backend persists-before-
  //    responding. If a race ever shows up (toggle still "No" right after accept,
  //    because the GET read stale state), move this call into the
  //    `TradeRequestInfoUpdated` SignalR handler in MegaPipe.tsx — that push is
  //    raised AFTER the backend has fully processed, so the GET is guaranteed
  //    fresh. Re-hydrate there only when that esid's settings panel is open.
  if (accepted) {
    await getEditSettingsAndAddToStore({ esid, getAccessTokenSilently });
  }

  return response;
}

// ----- Reconcile request status when an Advise setting changes (LQC-1842/1843) --

// Pure: given a field's CURRENT negotiation status and the NEW Advise value the OP
// just submitted, return the accept/reject boolean to record, or null for no-op.
//   • → Yes (release): an outstanding Requested OR a prior Rejected becomes
//     Accepted (True). False = nothing was asked; True = already released → no-op.
//   • → No (withdraw): a released field (True) becomes Rejected. Everything else
//     is a no-op (LQC-1842 only acts on the released flag).
// Exported for unit testing.
export function resolveSettingReconcile(
  current: RequestInfoStatus,
  releaseToYes: boolean,
): boolean | null {
  if (releaseToYes) {
    if (
      current === RequestInfoStatus.Requested ||
      current === RequestInfoStatus.Rejected
    ) {
      return true;
    }
    return null;
  }
  if (current === RequestInfoStatus.True) return false;
  return null;
}

// LQC-1842 / LQC-1843 — after the OP submits Advise-setting changes via the
// slide-in Update (patchSidebarTradeSettings), keep the request negotiation status
// in sync with what was just released/withheld. Takes the SAME per-CP payloads the
// settings PATCH submitted ({ executionRequestId, releaseX: boolean, ... }); for
// each reconcilable field it reads the current status, applies resolveSettingReconcile,
// and records the response against /Trade/Execution/Request ONLY (the setting was
// already persisted by the Update). Skips releaseSize and any non-request field.
export async function reconcileRequestStatusesAfterSettingsUpdate({
  esid,
  payloads,
  getAccessTokenSilently,
}: {
  esid: string;
  // Accepts the array patchSidebarTradeSettings returns. Its `.filter(Boolean)`
  // leaves `(payload | null)[]` in TS's view, so null elements are allowed here
  // and skipped at runtime below.
  payloads:
    | ({ executionRequestId?: number | string; [key: string]: any } | null)[]
    | undefined
    | null;
  getAccessTokenSilently: Function;
}) {
  for (const payload of payloads ?? []) {
    if (!payload || payload.executionRequestId == null) continue;
    const executionRequestId = payload.executionRequestId;

    for (const settingField of Object.keys(SETTING_TO_REQUEST_FIELD)) {
      if (typeof payload[settingField] !== "boolean") continue;
      const field = SETTING_TO_REQUEST_FIELD[settingField];
      const current = selectFieldStatus(
        esid,
        executionRequestId,
        field,
      )(requestInformationStore.getState());
      const accepted = resolveSettingReconcile(current, payload[settingField]);
      if (accepted === null) continue;

      await recordRequestResponse({
        esid,
        executionRequestId,
        field,
        accepted,
        getAccessTokenSilently,
      });
    }
  }
}
